/**
 * Sales Calendar Page
 * Manager view — shows all sales rep events and availability in a calendar.
 * Left sidebar: rep filter panel. Main area: calendar view.
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getSalesPositions,
  collectSubtreePositionIds,
} from "../services/salesTeam.service";
import { getCalendarData } from "../services/salesCalendar.service";
import SalesCalendarView from "../components/salesCalendar/SalesCalendarView";
import RepFilterPanel from "../components/salesCalendar/RepFilterPanel";
import CalendarEventPopover from "../components/salesCalendar/CalendarEventPopover";
import AvailabilityModal from "../components/salesCalendar/AvailabilityModal";
import AssignRepModal from "../components/salesCalendar/AssignRepModal";
import EventsRepFilter from "../components/events/EventsRepFilter";
import { useAuth } from "../contexts/AuthContext";
import { useSchedulingDelegations } from "../hooks/useSchedulingDelegations";
import { canAssignEventSalesReps } from "../utils/salesRepAssignPermission";
import {
  TERRITORY_MANAGER_POSITION_TYPES,
  REGIONAL_MANAGER_POSITION_TYPES,
  SALES_DIRECTOR_POSITION_TYPES,
} from "../utils/salesPositionDisplay";
import type {
  CalendarEvent,
  SalesRepAvailability,
} from "../types/salesCalendar.types";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Button from "../components/common/Button";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { Views } from "react-big-calendar";
import { format, startOfMonth, endOfMonth } from "date-fns";

const SalesCalendar: React.FC = () => {
  const { user } = useAuth();
  // mig 1780: a scheduling delegate who is not staff (an MSR) sees the team they schedule for, and opens Confirm
  // Schedule instead of the event page
  const { delegations, isDelegate, teamPositionIds } =
    useSchedulingDelegations();
  const delegateOnly = isDelegate && !canAssignEventSalesReps(user?.role);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<string>(Views.MONTH);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  // Territory-hierarchy filter (SA-5 pickers + My Territory preset). Selecting a
  // TM/RM/SD node — or "My Territory" — scopes the calendar to that node's
  // downline subtree. Pickers and My Territory are mutually exclusive modes.
  const [tmId, setTmId] = useState("");
  const [rmId, setRmId] = useState("");
  const [sdId, setSdId] = useState("");
  const [myTerritory, setMyTerritory] = useState(false);
  const [showEvents, setShowEvents] = useState(true);
  const [showAvailability, setShowAvailability] = useState(true);
  const [showCalendarRequests, setShowCalendarRequests] = useState(true);

  // Modal state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [assignRepTarget, setAssignRepTarget] = useState<{
    eventId: string;
    eventTitle: string;
  } | null>(null);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<
    SalesRepAvailability | undefined
  >();
  const [availabilityPreDate, setAvailabilityPreDate] = useState<
    string | undefined
  >();

  // Fetch all active positions
  const { data: allPositions = [], isLoading: positionsLoading } = useQuery({
    queryKey: ["sales-positions-active"],
    queryFn: () => getSalesPositions({ is_active: true }),
    select: (data) =>
      data.filter(
        (p) =>
          p.position_type === "sales_rep" ||
          p.position_type === "territory_manager" ||
          p.position_type === "regional_manager" ||
          p.position_type === "director",
      ),
  });
  const positions = useMemo(
    () =>
      delegateOnly
        ? allPositions.filter((p) => teamPositionIds.has(p.id))
        : allPositions,
    [delegateOnly, allPositions, teamPositionIds],
  );

  // The logged-in user's own sales position (if any) — backs "My Territory".
  const myPosition = useMemo(
    () =>
      user?.id
        ? (positions.find((p) => p.assigned_profile_id === user.id) ?? null)
        : null,
    [positions, user?.id],
  );

  // Resolve the active territory scope → set of position IDs (the selected
  // node + its downline). My Territory wins; otherwise the scope is the
  // INTERSECTION of every selected picker's subtree (so TM-under-RM narrows to
  // the TM, and inconsistent picks honestly resolve to empty), mirroring the
  // AND semantics of the SA-5 events filter. null = no scope = all reps.
  const scopeIds = useMemo<Set<string> | null>(() => {
    if (myTerritory && myPosition) {
      return collectSubtreePositionIds(myPosition.id, positions);
    }
    const roots = [sdId, rmId, tmId].filter(Boolean);
    if (roots.length === 0) {
      return null;
    }
    let acc: Set<string> | null = null;
    for (const root of roots) {
      const sub = collectSubtreePositionIds(root, positions);
      acc = acc === null ? sub : new Set([...acc].filter((id) => sub.has(id)));
    }
    return acc;
  }, [myTerritory, myPosition, sdId, rmId, tmId, positions]);

  // Reps visible in the panel / available for selection given the scope.
  const visiblePositions = useMemo(
    () => (scopeIds ? positions.filter((p) => scopeIds.has(p.id)) : positions),
    [positions, scopeIds],
  );

  // Initialize selection to all reps once positions load (first paint only).
  useEffect(() => {
    if (!initialized && positions.length > 0) {
      setSelectedIds(new Set(positions.map((p) => p.id)));
      setInitialized(true);
    }
  }, [initialized, positions]);

  // When the territory scope changes, snap the rep selection to that scope (or
  // back to all reps when cleared). Keyed on the scope selection only, so manual
  // checkbox toggles WITHIN a scope are preserved.
  const scopeKey = useMemo(
    () =>
      `${myTerritory ? "me" : ""}|${[sdId, rmId, tmId]
        .filter(Boolean)
        .sort()
        .join(",")}`,
    [myTerritory, sdId, rmId, tmId],
  );
  useEffect(() => {
    if (!initialized) {
      return;
    }
    setSelectedIds(
      scopeIds ? new Set(scopeIds) : new Set(positions.map((p) => p.id)),
    );
    // Snap only when the scope SELECTION changes — not on every positions/scopeIds
    // identity change — so in-scope manual toggles survive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Picking a TM/RM/SD clears My Territory (mutually exclusive modes).
  const pick = useCallback(
    (setter: (id: string) => void) => (id: string) => {
      setMyTerritory(false);
      setter(id);
    },
    [],
  );

  const handleMyTerritory = useCallback(() => {
    setTmId("");
    setRmId("");
    setSdId("");
    setMyTerritory(true);
  }, []);

  const clearScope = useCallback(() => {
    setTmId("");
    setRmId("");
    setSdId("");
    setMyTerritory(false);
  }, []);

  // Date range for current view (buffer 1 week each side for month view)
  const dateRange = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    // Add buffer for events that span into adjacent months
    const start = format(
      new Date(monthStart.getTime() - 7 * 86400000),
      "yyyy-MM-dd",
    );
    const end = format(
      new Date(monthEnd.getTime() + 7 * 86400000),
      "yyyy-MM-dd",
    );
    return { start, end };
  }, [currentDate]);

  const selectedPositionIds = useMemo(
    () => Array.from(selectedIds),
    [selectedIds],
  );

  // Fetch calendar data
  const { data: calendarEvents = [], isLoading: calendarLoading } = useQuery({
    queryKey: [
      "sales-calendar",
      selectedPositionIds,
      dateRange,
      showEvents,
      showAvailability,
      showCalendarRequests,
    ],
    queryFn: () =>
      getCalendarData(selectedPositionIds, dateRange, {
        showEvents,
        showAvailability,
        showCalendarRequests,
      }),
    enabled: selectedPositionIds.length > 0,
  });

  // Handlers
  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(visiblePositions.map((p) => p.id)));
  }, [visiblePositions]);

  const handleSelectNone = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
  }, []);

  // Owner 2026-10-27 (MSR R2): no new time-off entry here (the "Add Availability" button is hidden), so clicking an
  // empty slot no longer opens the time-off dialog either. Existing blocks can still be opened and edited/removed.
  const handleSelectSlot = useCallback(
    (_slotInfo: { start: Date; end: Date }) => undefined,
    [],
  );

  const handleEditAvailability = useCallback((avail: SalesRepAvailability) => {
    setEditingAvailability(avail);
    setAvailabilityPreDate(undefined);
    setShowAvailabilityModal(true);
  }, []);

  const handleAssignRep = useCallback((eventId: string, eventTitle: string) => {
    setAssignRepTarget({ eventId, eventTitle });
  }, []);

  // Route button: collect physical events on the current day with valid addresses
  const routeEvents = useMemo(() => {
    const dayStr = format(currentDate, "yyyy-MM-dd");
    return calendarEvents
      .filter((e) => {
        if (e.resource.type !== "event") {
          return false;
        }
        return format(e.start, "yyyy-MM-dd") === dayStr;
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }, [calendarEvents, currentDate]);

  const routeUrl = useMemo(() => {
    const addresses: string[] = [];
    for (const ev of routeEvents) {
      const raw = ev.resource.originalData as any;
      // Join-table: raw.event.location_address; Direct: raw.location_address
      const addr = raw?.event?.location_address || raw?.location_address;
      if (!addr || typeof addr === "string") {
        continue;
      }
      const parts = [
        (addr as Record<string, string>).address_line1,
        (addr as Record<string, string>).city,
        (addr as Record<string, string>).state,
        (addr as Record<string, string>).postal_code,
      ].filter(Boolean);
      if (parts.length >= 2) {
        addresses.push(parts.join(", "));
      }
    }
    // Deduplicate (multiple reps at same event/location)
    const unique = [...new Set(addresses)];
    if (unique.length < 2) {
      return null;
    }
    return (
      "https://www.google.com/maps/dir/" +
      unique.map((a) => encodeURIComponent(a)).join("/")
    );
  }, [routeEvents]);

  const handleRoute = useCallback(() => {
    if (routeUrl) {
      window.open(routeUrl, "_blank");
    }
  }, [routeUrl]);

  if (positionsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-white">
        <h1 className="text-xl font-bold text-gray-900">Sales Calendar</h1>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={handleRoute}
            disabled={currentView !== Views.DAY || !routeUrl}
            title={
              currentView !== Views.DAY
                ? "Switch to Day view to use Route"
                : !routeUrl
                  ? "Need 2+ events with addresses on this day"
                  : `Route ${routeEvents.length} events`
            }
          >
            <MapPinIcon className="h-4 w-4 mr-1" />
            Route
          </Button>
          {/* Owner 2026-10-27 (MSR R2): the manager "Add Availability" (time-off blocks) button is hidden. MSRs now mark
              the days they CAN work (MSR Team > Availability); time off is no longer entered here. */}
        </div>
      </div>

      {delegateOnly && (
        <div
          className="px-6 py-2 border-b bg-blue-50 text-sm text-blue-900"
          data-testid="scheduling-delegate-banner"
        >
          You schedule for{" "}
          {delegations
            .map((d) => `${d.holder_name || d.position_title}'s team`)
            .join(", ")}
          . Open an event to confirm its schedule or assign reps.
        </div>
      )}

      {/* Territory-hierarchy filter bar (not for a delegate-only user: their view is the team they schedule for) */}
      {!delegateOnly && (
        <div className="flex items-center gap-3 px-6 py-2 border-b bg-gray-50 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Territory
          </span>
          <Button
            size="sm"
            variant={myTerritory ? "primary" : "secondary"}
            onClick={handleMyTerritory}
            disabled={!myPosition}
            title={
              myPosition
                ? `Scope to your territory (${myPosition.assigned_name || myPosition.title})`
                : "No sales position is linked to your account"
            }
          >
            My Territory
          </Button>
          <EventsRepFilter
            label="TM"
            value={tmId}
            onChange={pick(setTmId)}
            allowedTypes={TERRITORY_MANAGER_POSITION_TYPES}
          />
          <EventsRepFilter
            label="RM"
            value={rmId}
            onChange={pick(setRmId)}
            allowedTypes={REGIONAL_MANAGER_POSITION_TYPES}
          />
          <EventsRepFilter
            label="SD"
            value={sdId}
            onChange={pick(setSdId)}
            allowedTypes={SALES_DIRECTOR_POSITION_TYPES}
          />
          {scopeIds && (
            <>
              <button
                type="button"
                onClick={clearScope}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Show all reps
              </button>
              <span className="text-xs text-gray-500">
                {visiblePositions.length} rep
                {visiblePositions.length === 1 ? "" : "s"} in territory
              </span>
            </>
          )}
        </div>
      )}

      {/* Main content */}
      {/* Phone: the rep panel stacks above the calendar so the month grid gets the full width. */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0 min-w-0">
        {/* Sidebar */}
        <RepFilterPanel
          positions={visiblePositions}
          selectedIds={selectedIds}
          onToggle={handleToggle}
          onSelectAll={handleSelectAll}
          onSelectNone={handleSelectNone}
          showEvents={showEvents}
          showAvailability={showAvailability}
          showCalendarRequests={showCalendarRequests}
          onToggleEvents={() => setShowEvents((v) => !v)}
          onToggleAvailability={() => setShowAvailability((v) => !v)}
          onToggleCalendarRequests={() => setShowCalendarRequests((v) => !v)}
        />

        {/* Calendar */}
        <div className="flex flex-col flex-1 p-2 md:p-4 min-h-[520px] md:min-h-0 min-w-0 relative">
          {calendarLoading && selectedPositionIds.length > 0 && (
            <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
              <LoadingSpinner />
            </div>
          )}
          {selectedPositionIds.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              Select at least one rep to view the calendar
            </div>
          ) : (
            <SalesCalendarView
              events={calendarEvents}
              currentDate={currentDate}
              currentView={currentView}
              onNavigate={setCurrentDate}
              onView={setCurrentView}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
            />
          )}
        </div>
      </div>

      {/* Event popover */}
      {selectedEvent && (
        <CalendarEventPopover
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEditAvailability={delegateOnly ? undefined : handleEditAvailability}
          onAssignRep={handleAssignRep}
          confirmScheduleOnly={delegateOnly}
        />
      )}

      {/* Assign rep modal */}
      {assignRepTarget && (
        <AssignRepModal
          eventId={assignRepTarget.eventId}
          eventTitle={assignRepTarget.eventTitle}
          onClose={() => setAssignRepTarget(null)}
        />
      )}

      {/* Availability modal */}
      {showAvailabilityModal && (
        <AvailabilityModal
          onClose={() => {
            setShowAvailabilityModal(false);
            setEditingAvailability(undefined);
          }}
          positions={positions}
          preSelectedDate={availabilityPreDate}
          existing={editingAvailability}
        />
      )}
    </div>
  );
};

export default SalesCalendar;
